import React, { useState, useMemo } from 'react';
import { Calculator } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';

const fmt = (n) => {
  if (!isFinite(n)) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
};

const MortgageCalculator = () => {
  const [price, setPrice] = useState(600000);
  const [down, setDown] = useState(120000);
  const [rate, setRate] = useState(6.75);
  const [years, setYears] = useState(30);
  const [taxRate, setTaxRate] = useState(0.91); // Pierce County default
  const [insurance, setInsurance] = useState(1500);
  const [hoa, setHoa] = useState(0);

  const result = useMemo(() => {
    const loan = Math.max(0, Number(price) - Number(down));
    const r = Number(rate) / 100 / 12;
    const n = Number(years) * 12;
    const piMonthly = r === 0 ? loan / n : (loan * r) / (1 - Math.pow(1 + r, -n));
    const taxMonthly = (Number(price) * (Number(taxRate) / 100)) / 12;
    const insMonthly = Number(insurance) / 12;
    const total = piMonthly + taxMonthly + insMonthly + Number(hoa);
    return { loan, piMonthly, taxMonthly, insMonthly, total };
  }, [price, down, rate, years, taxRate, insurance, hoa]);

  return (
    <section className="py-20 bg-white border-t border-gray-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <p className="text-xs tracking-[0.3em] text-gray-500 uppercase mb-3 flex items-center justify-center gap-2">
            <Calculator size={14} /> Mortgage Calculator
          </p>
          <h2 className="text-3xl md:text-4xl font-light text-gray-900">
            Estimate Your Monthly Payment
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto mt-4">
            Tacoma and Pierce County tax rate defaults loaded. Adjust for your target area.
            Estimate only — consult a lender for accurate quotes.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          <div className="space-y-4 bg-[#f7f6f3] border border-gray-200 rounded-xl p-6">
            <div>
              <Label htmlFor="mc-price" className="text-xs tracking-widest uppercase text-gray-600">Purchase Price</Label>
              <Input id="mc-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} min="0" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mc-down" className="text-xs tracking-widest uppercase text-gray-600">Down Payment</Label>
                <Input id="mc-down" type="number" value={down} onChange={(e) => setDown(e.target.value)} min="0" />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-gray-600">Down %</Label>
                <div className="h-10 flex items-center px-3 text-sm text-gray-700 border border-gray-200 rounded bg-white">
                  {price > 0 ? ((Number(down) / Number(price)) * 100).toFixed(1) : '0'}%
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mc-rate" className="text-xs tracking-widest uppercase text-gray-600">Interest Rate %</Label>
                <Input id="mc-rate" type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} min="0" />
              </div>
              <div>
                <Label htmlFor="mc-years" className="text-xs tracking-widest uppercase text-gray-600">Term (Years)</Label>
                <Input id="mc-years" type="number" value={years} onChange={(e) => setYears(e.target.value)} min="1" max="40" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mc-tax" className="text-xs tracking-widest uppercase text-gray-600">Property Tax %</Label>
                <Input id="mc-tax" type="number" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} min="0" />
              </div>
              <div>
                <Label htmlFor="mc-ins" className="text-xs tracking-widest uppercase text-gray-600">Insurance / Year</Label>
                <Input id="mc-ins" type="number" value={insurance} onChange={(e) => setInsurance(e.target.value)} min="0" />
              </div>
            </div>
            <div>
              <Label htmlFor="mc-hoa" className="text-xs tracking-widest uppercase text-gray-600">HOA / Month</Label>
              <Input id="mc-hoa" type="number" value={hoa} onChange={(e) => setHoa(e.target.value)} min="0" />
            </div>
          </div>

          <div className="bg-[#0a0a0a] text-white rounded-xl p-8 space-y-5">
            <p className="text-xs tracking-[0.3em] text-gray-400 uppercase">Estimated Monthly Payment</p>
            <p className="text-5xl font-light">{fmt(result.total)}</p>
            <div className="w-12 h-[1px] bg-white/20" />
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-400">Principal &amp; Interest</dt>
                <dd>{fmt(result.piMonthly)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-400">Property Tax</dt>
                <dd>{fmt(result.taxMonthly)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-400">Insurance</dt>
                <dd>{fmt(result.insMonthly)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-400">HOA</dt>
                <dd>{fmt(Number(hoa))}</dd>
              </div>
              <div className="flex justify-between border-t border-white/10 pt-3">
                <dt className="text-gray-400">Loan Amount</dt>
                <dd>{fmt(result.loan)}</dd>
              </div>
            </dl>
            <p className="text-xs text-gray-500 leading-relaxed pt-4">
              Estimate only. Actual payment varies with lender fees, PMI, exact tax assessment, and insurance quotes.
              Contact me for a connection to a trusted local lender.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MortgageCalculator;
